import formatPhone from 'src/functions/formatPhone'
import formatPrice from 'src/functions/formatPrice'
import getLpuAvatarUrl, { getLpuLogoImageFiles } from 'src/utils/getLpuAvatarUrl'
import declensionByNum from 'src/utils/declensionByNum'
import type {
  AnalysesLpuBidPlacement,
  AnalysesLpuCard,
  AnalysesLpuMetro,
  AnalysesLpuPriceItem,
} from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCard/types'
import type {
  LpuCardsPageBidPlacement,
  LpuCardsPageItem,
  LpuCardsPageMetro,
} from 'src/components/pages/[town-translit]/diagnostika/analiz/_types'

const CLOSED_LOGO_URL = '/static/_v1/pd/pictures/stubs/does-not-work-240x80.svg'
const METRO_LIMIT = 3
const CLOSING_TIME_LIMIT_MINUTES = 180

type MapLpuCardPropsOptions = {
  serviceClassTranslit: string
  country: {
    iso: string
  }
  currencySymbol?: string
  currentAwardYear?: number
  townTranslit?: string
  isSuperuser?: boolean
  getLpuPriceHref: (_item: LpuCardsPageItem) => string
  getLpuPageHref: (_item: LpuCardsPageItem) => string
  getLpuReviewsHref: (_item: LpuCardsPageItem) => string
}

export const getReviewsCount = (lpu: LpuCardsPageItem) => (
  (lpu.rates ?? 0) + (lpu.drrates ?? 0)
)

const getAnalysesCount = (lpu: LpuCardsPageItem) => (
  lpu.analyses_count
  ?? lpu.prices_count
  ?? lpu.number_of_prices
  ?? 0
)

const formatMetroDistance = (metro: LpuCardsPageMetro) => {
  if (typeof metro.walk_distance === 'number') {
    return `${Math.round(metro.walk_distance * 1000)} м`
  }

  if (typeof metro.walk_time !== 'number') {
    return ''
  }

  if (metro.walk_time < 60) {
    return '1 мин'
  }

  return `${Math.floor(metro.walk_time / 60)} мин`
}

const mapMetros = (lpu: LpuCardsPageItem): AnalysesLpuMetro[] => {
  if (lpu.closed || !lpu.nearest_metros?.length) {
    return []
  }

  return lpu.nearest_metros
    .slice(0, METRO_LIMIT)
    .map(metro => ({
      name: metro.metro?.name ?? '',
      distance: formatMetroDistance(metro),
      color: metro.metro?.color ?? '#607A93',
    }))
    .filter(metro => metro.name)
}

const getScheduleText = (lpu: LpuCardsPageItem) => {
  const lpuAddress = lpu.lpu_addresses?.[0]

  return lpuAddress?.schedule_status
    || lpuAddress?.schedule_str
    || lpu.schedule_str
    || ''
}

const getPhone = (lpu: LpuCardsPageItem, country: MapLpuCardPropsOptions['country']) => {
  if (!lpu.appointment_service_express || !lpu.lpu_phone) {
    return undefined
  }

  return formatPhone({
    phone: lpu.lpu_phone,
    country,
  })
}

const getClosingTimeText = (minutesUntilClose?: number | null) => {
  if (!minutesUntilClose || minutesUntilClose >= CLOSING_TIME_LIMIT_MINUTES) {
    return undefined
  }

  const mins = Math.floor(minutesUntilClose)

  if (mins < 60) {
    return `До закрытия ${mins} ${declensionByNum(mins, ['минута', 'минуты', 'минут'])}`
  }

  let hours = Math.ceil(mins / 60)

  if (mins % 60 === 0) {
    hours += 1
  }

  return `До закрытия меньше ${hours} часов`
}

const getBidPlacementList = (lpu: LpuCardsPageItem): LpuCardsPageBidPlacement[] => {
  if (!lpu.bid_placement) {
    return []
  }

  return Array.isArray(lpu.bid_placement)
    ? lpu.bid_placement
    : [lpu.bid_placement]
}

const mapBidPlacement = (lpu: LpuCardsPageItem): AnalysesLpuBidPlacement | null => {
  const [firstPlacement] = getBidPlacementList(lpu)

  if (!firstPlacement) {
    return null
  }

  return {
    bid: firstPlacement.bid,
    place: firstPlacement.place,
    paymentSumma: firstPlacement.payment?.summa ?? null,
    adminUrl: firstPlacement.get_detail_admin_url,
  }
}

const mapPriceItems = (
  lpu: LpuCardsPageItem,
  currencySymbol?: string,
): AnalysesLpuPriceItem[] => {
  if (lpu.cheapest_synonyms?.length) {
    return lpu.cheapest_synonyms.map(item => ({
      name: item.synonym_name,
      price: formatPrice({
        price: item.price,
        currencySymbol,
      }),
    }))
  }

  if (lpu.service_prices?.length) {
    return lpu.service_prices.map(item => ({
      name: item.dtype?.name ?? '',
      price: formatPrice({
        price: item.price,
        currencySymbol,
      }),
      withPreposition: true,
    })).filter(item => item.name)
  }

  return []
}

const mapScheduleItem = (
  lpu: LpuCardsPageItem,
  {
    country,
    townTranslit,
  }: Pick<MapLpuCardPropsOptions, 'country' | 'townTranslit'>,
) => {
  const synonym = lpu.synonym_price_data?.[0]
  const services: Record<string, unknown> = {
    lpuPhone: lpu.lpu_phone
      ? formatPhone({
        phone: lpu.lpu_phone,
        country,
        isRaw: true,
      })
      : '',
    lpuId: String(lpu.id),
    serviceIsAvailable: String(Boolean(lpu.service_available)),
    serviceIsDirect: String(Boolean(lpu.has_appointment_service_direct)),
    serviceIsExpress: String(Boolean(lpu.has_appointment_service_express)),
    lpuWorkingText: lpu.nearest_appointment_express_datetime || '',
    isEnabledLpuClub: lpu.is_active_club_discount_on_diagnostics ? 'true' : 'false',
  }

  if (synonym?.synonym) {
    services.synonymPreview = {
      serviceData: {
        ...(synonym.doctor_specialities?.length
          ? {
            doctorSpecialities: synonym.doctor_specialities.map(speciality => ({
              name: speciality.name,
              translit: speciality.translit,
            })),
            townTranslit: townTranslit || lpu.town.translit,
          }
          : {}),
        allowTreatmentReferral: Boolean(synonym.allow_treatment_referral),
        requiresPrimaryConsultation: Boolean(synonym.requires_primary_consultation),
        hasPrimaryConsultationDoctors: Boolean(synonym.has_primary_consultation_doctors),
      },
      id: synonym.synonym.id ?? null,
      name: synonym.name || '',
      duration: synonym.duration ?? null,
      price: synonym.price ?? null,
      priceWithDiscount: synonym.price_with_club != null
        ? Math.round(synonym.price_with_club)
        : null,
      dtypeId: synonym.synonym_dtype_id ?? null,
      hasIntervals: Boolean(synonym.has_intervals),
    }
  }

  return {
    services,
  }
}

export default function mapLpuCardProps(
  lpu: LpuCardsPageItem,
  {
    serviceClassTranslit,
    country,
    currencySymbol,
    currentAwardYear,
    townTranslit,
    isSuperuser,
    getLpuPriceHref,
    getLpuPageHref,
    getLpuReviewsHref,
  }: MapLpuCardPropsOptions,
): AnalysesLpuCard {
  const logoAlt = lpu.town.name
    ? `${lpu.name}, ${lpu.town.name}`
    : lpu.name
  const lpuPageHref = getLpuPageHref(lpu)
  const lpuReviewsHref = getLpuReviewsHref(lpu)
  const bidPlacementList = getBidPlacementList(lpu)
  const hasSpecialPlacement = Boolean(lpu.bid || bidPlacementList.length)

  return {
    id: lpu.id,
    name: lpu.name,
    href: `${getLpuPriceHref(lpu)}#${serviceClassTranslit}`,
    lpuPageHref,
    lpuTypeName: lpu.lputype?.name ?? '',
    logoUrl: lpu.closed
      ? CLOSED_LOGO_URL
      : getLpuAvatarUrl({ lpu, isLarge: true }),
    logoFiles: getLpuLogoImageFiles(lpu),
    logoAlt,
    rating: lpu.closed ? 0 : (lpu.stars ?? 0),
    reviewsCount: lpu.closed ? 0 : getReviewsCount(lpu),
    reviewsHref: `${lpuReviewsHref}#otzivi`,
    analysesCount: getAnalysesCount(lpu),
    lpuLoyaltyProgram: lpu.lpu_loyalty_program || undefined,
    address: lpu.address ?? '',
    metros: mapMetros(lpu),
    scheduleText: getScheduleText(lpu),
    scheduleAddressId: lpu.lpu_addresses?.[0]?.id,
    phone: getPhone(lpu, country),
    reviewsSummary: lpu.positive_rate || undefined,
    closed: Boolean(lpu.closed),
    isSpecialPlacement: hasSpecialPlacement,
    isUltima: lpu.owner?.soft_version === 'ULTIMA',
    isClubDiscountEnabled: Boolean(lpu.is_active_club_discount_on_diagnostics),
    scheduleItemJson: JSON.stringify(mapScheduleItem(lpu, { country, townTranslit })),
    hasVideoCard: Boolean(lpu.is_exists_video_card),
    isAwardWinner: Boolean(lpu.is_award_or_grand_prix_winner),
    awardRedirectUrl: `${lpuPageHref}#medals`,
    currentAwardYear: currentAwardYear ?? 0,
    closingTimeText: getClosingTimeText(lpu.minutes_until_close),
    license: !lpu.closed && lpu.license ? lpu.license : undefined,
    isActiveClubDiscount: Boolean(lpu.is_active_club_discount_on_diagnostics),
    priceItems: mapPriceItems(lpu, currencySymbol),
    bidPlacement: mapBidPlacement(lpu),
    isSuperuser: Boolean(isSuperuser),
  }
}
